<template>
  <div class="uuid-generator">
    <h1>UUID/GUID 生成器</h1>
    
    <div class="generator-container">
      <!-- 生成选项 -->
      <div class="options-section">
        <div class="option-group">
          <span>UUID 版本：</span>
          <el-radio-group v-model="version" @change="generateUuid">
            <el-radio-button label="4">v4 (随机)</el-radio-button>
            <el-radio-button label="1">v1 (时间)</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <span>格式：</span>
          <el-radio-group v-model="format" @change="formatUuid">
            <el-radio-button label="standard">标准格式</el-radio-button>
            <el-radio-button label="simple">简单格式</el-radio-button>
            <el-radio-button label="base64">Base64</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <span>大小写：</span>
          <el-radio-group v-model="letterCase" @change="formatUuid">
            <el-radio-button label="lower">小写</el-radio-button>
            <el-radio-button label="upper">大写</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <span>生成数量：</span>
          <el-input-number
            v-model="quantity"
            :min="1"
            :max="100"
            @change="generateMultiple"
          />
        </div>
      </div>

      <!-- 生成结果 -->
      <div class="result-section">
        <div class="section-header">
          <h3>生成结果</h3>
          <div class="actions">
            <el-button type="primary" @click="generateMultiple">
              重新生成
            </el-button>
            <el-button @click="copyAll">
              全部复制
            </el-button>
          </div>
        </div>

        <div class="uuid-list">
          <div
            v-for="(uuid, index) in uuids"
            :key="index"
            class="uuid-item"
          >
            <el-input
              v-model="uuids[index]"
              readonly
            >
              <template #append>
                <el-button @click="copyUuid(uuid)">复制</el-button>
              </template>
            </el-input>
          </div>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>说明</h3>
        <ul>
          <li>UUID v4 使用加密随机数生成，适合大多数场景</li>
          <li>UUID v1 基于时间戳和MAC地址生成，可保证时序性</li>
          <li>支持标准格式、无分隔符和Base64编码输出</li>
          <li>可批量生成多个UUID</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { v4 as uuidv4, v1 as uuidv1 } from 'uuid'

const version = ref('4')
const format = ref('standard')
const letterCase = ref('lower')
const quantity = ref(1)
const uuids = ref([])

// 生成单个UUID
const generateUuid = () => {
  const uuid = version.value === '4' ? uuidv4() : uuidv1()
  return formatUuidString(uuid)
}

// 格式化UUID字符串
const formatUuidString = (uuid) => {
  let formatted = uuid
  
  // 处理格式
  if (format.value === 'simple') {
    formatted = formatted.replace(/-/g, '')
  } else if (format.value === 'base64') {
    formatted = btoa(formatted.replace(/-/g, ''))
  }
  
  // 处理大小写
  formatted = letterCase.value === 'upper' ? 
    formatted.toUpperCase() : 
    formatted.toLowerCase()
    
  return formatted
}

// 格式化现有UUID
const formatUuid = () => {
  uuids.value = uuids.value.map(uuid => {
    // 先转换回标准格式
    let standard = uuid
    if (format.value !== 'standard') {
      standard = version.value === '4' ? uuidv4() : uuidv1()
    }
    return formatUuidString(standard)
  })
}

// 生成多个UUID
const generateMultiple = () => {
  uuids.value = Array(quantity.value)
    .fill(null)
    .map(() => generateUuid())
}

// 复制单个UUID
const copyUuid = async (uuid) => {
  try {
    await navigator.clipboard.writeText(uuid)
    ElMessage.success('复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 复制所有UUID
const copyAll = async () => {
  if (uuids.value.length === 0) {
    ElMessage.warning('没有可复制的内容')
    return
  }

  try {
    const text = uuids.value.join('\n')
    await navigator.clipboard.writeText(text)
    ElMessage.success('全部复制成功')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

// 初始生成
generateMultiple()
</script>

<style scoped>
.uuid-generator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.generator-container {
  margin-top: 20px;
}

.options-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.result-section {
  margin: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.actions {
  display: flex;
  gap: 10px;
}

.uuid-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.uuid-item {
  display: flex;
  align-items: center;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-input__inner) {
  font-family: monospace;
  font-size: 14px;
}

:deep(.el-input-group__append) {
  padding: 0;
}

:deep(.el-input-group__append button) {
  border: none;
  margin: 0;
  height: 100%;
}
</style> 